// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { WorkSheet, WorkBook, utils, writeFile, read } from 'xlsx';

@Injectable()
export class XLSXService {
  public defaultExtension = 'xlsx';

  /**
   * Exports an array of data to excel.
   *
   * @param filename The download file name
   * @param data An array of data to be exported
   * @param workSheetName Name of the worksheet
   * @param columnFormats An array containing the cell encodings for columns
   * @param metaData An array of data to be printed before the core data dump
   * @return whether or not the convertion to xlsx was succesful
   */
  public arrayToXLSX(
    fileName: string,
    data: any[],
    workSheetName?: string,
    columnFormats?: ColumnFormat[],
    metaData?: any[],
  ): boolean {
    try {
      let workbook;
      if (metaData) {
        workbook = this.manuallyBuildWorkbook(columnFormats, data, metaData);
      } else {
        let worksheet: WorkSheet = utils.json_to_sheet(data, {
          dateNF: 'yyyy-mm-dd',
        });

        if (columnFormats?.length > 0 && !metaData) {
          worksheet = this.setColumnFormats(columnFormats, worksheet);
        }

        let sheets;
        let sheetNames;
        if (workSheetName && workSheetName !== '') {
          sheets = { [workSheetName]: worksheet };
          sheetNames = [workSheetName];
        } else {
          sheets = { data: worksheet };
          sheetNames = ['data'];
        }

        workbook = {
          Sheets: sheets,
          SheetNames: sheetNames,
        };
      }

      writeFile(workbook, `${fileName}.${this.defaultExtension}`);
    } catch (e) {
      return false;
    }

    return true;
  }

  /**
   * Builds a workbook from metaData and main data dump
   *
   * @param columnFormats An array containing the cell encodings for columns
   * @param data An array of data containing the main data dump
   * @param metaData An array of data to be printed before the core data dump
   * @return XLSX.WorkBook The workbook ready for download
   */
  private manuallyBuildWorkbook(
    columnFormats: ColumnFormat[],
    data: any[],
    metaData: any[],
  ): WorkBook {
    var wb = utils.book_new();
    wb.SheetNames.push('Data');

    // calculate what row to add main data dump, and where to start encoding cells
    const startData = metaData.length + 2;

    // add meta data to sheet
    let ws = utils.aoa_to_sheet(metaData);

    // add main data to sheet
    utils.sheet_add_json(ws, data, {
      origin: 'A' + startData,
    });

    ws = this.setColumnFormats(columnFormats, ws, startData);

    wb.Sheets['Data'] = ws;

    return wb;
  }

  /**
   * Sets column cells ecoding
   *
   * @param columnFormats An array containing the cell encodings for columns
   * @param worksheet An XLSX.Worksheet to format
   * @param startRow what row to start the encoding
   * @return XLSX.WorkBook The workbook ready for download
   */
  private setColumnFormats(
    columnFormats: ColumnFormat[],
    worksheet: WorkSheet,
    startRow?: number,
  ): WorkSheet {
    let colArr = [];
    columnFormats.forEach((row: ColumnFormat) => {
      let format = '@'; // set cell ecoding to general as default
      if (row.format === 'date') {
        format = 'm/d/yy';
      }
      row.column.forEach((column: string) => {
        colArr.push({ column: utils.decode_col(column), format: format });
      });
    });

    const range = utils.decode_range(worksheet['!ref']);
    if (!startRow) {
      startRow = range.s.r;
    }
    for (var i = startRow; i <= range.e.r; ++i) {
      colArr.forEach((col: { column: number; format: string }) => {
        // loop through all the cells in the specified column and convert them to the desired encoding
        const ref1 = utils.encode_cell({ r: i, c: col.column });
        worksheet[ref1].z = col.format;
      });
    }

    return worksheet;
  }

  public downloadXLSX(fileName: string, sheets = [], ext = this.defaultExtension): boolean {
    try {
      if (sheets.length) {
        let sheetNames = [];
        let workSheets = {};

        for (let sheet of sheets) {
          let exportData = [];

          if (typeof sheet['sheetTitle'] !== 'undefined' && sheet['sheetTitle'].length) {
            exportData.push([sheet['sheetTitle']]);
          }

          if (typeof sheet['sheetHeaders'] !== 'undefined' && sheet['sheetHeaders'].length) {
            exportData.push(sheet['sheetHeaders']);
          }

          if (typeof sheet['sheetData'] !== 'undefined' && sheet['sheetData'].length) {
            for (let row of sheet['sheetData']) {
              exportData.push(row);
            }
          }

          let worksheet: WorkSheet = utils.aoa_to_sheet(exportData, { cellDates: true });

          sheetNames.push(sheet['sheetName']);
          workSheets[sheet['sheetName']] = worksheet;
        }

        const workbook: WorkBook = { Sheets: workSheets, SheetNames: sheetNames };
        writeFile(workbook, fileName + '.' + ext);
      }
    } catch (err) {
      return false;
    }

    return true;
  }

  public getDateTimeString(): string {
    const today = new Date();
    const date = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
    const time = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;

    return `${date} ${time}`;
  }

  public async fileToArray(file: File): Promise<any[]> {
    const reader: FileReader = new FileReader();

    // Read the file - returns a binary string
    reader.readAsBinaryString(file);

    // Return the result of the file read as an observable
    return new Promise((resolutionFunc) => {
      reader.onload = (e: any) => {
        const binaryFile: string = e.target.result;
        const workbook: WorkBook = read(binaryFile, {
          type: 'binary',
          cellDates: true,
          dateNF: 'yyyy-mm-dd;@',
        });

        resolutionFunc(
          workbook.SheetNames.map((sheetName: string, index: number) => {
            return utils.sheet_to_json(workbook.Sheets[sheetName], {
              header: 1,
            });
          }),
        );
      };
    });
  }
}

export type ColumnFormat = {
  column: string[];
  format: string;
};
